import { computed, onBeforeUnmount, ref, shallowRef, watch, type Ref, type ShallowRef } from 'vue';
import { createEmptyAnswers, type CompileOptions, type MatrixAnswers } from '@record-fill';
import { getGeneratedRecordForm, type GeneratedRecordForm } from '@/entities/GeneratedRecord';
import { useUserStore } from '@/entities/User';
import { useCustomToast } from '@/shared/lib';
import {
  correctionCompileOptions,
  countCorrectionChanges,
  liveCorrectionMarks,
  mergeUnsavedEdits,
} from './correction-answers';

export const CORRECTION_ENTER_ERROR_TITLE = 'Não foi possível ligar o modo correção.';
const PULSE_MS = 1000;

interface CorrectionModeSource {
  recordId?: Ref<string>;
  answers: Ref<MatrixAnswers>;
  openedAnswers: ShallowRef<MatrixAnswers | null>;
  canToggle: () => boolean;
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useCorrectionMode(source: CorrectionModeSource) {
  const { recordId, answers, openedAnswers } = source;
  const userStore = recordId ? useUserStore() : null;
  const { showErrorToast } = useCustomToast();

  const form = shallowRef<GeneratedRecordForm | null>(null);
  const active = ref(false);
  const pulsing = ref(false);
  const loading = ref(false);
  let generation = 0;
  let pulseTimer: ReturnType<typeof setTimeout> | undefined;

  const stored = computed(() =>
    form.value ? { ...createEmptyAnswers(form.value.matrix.id), ...form.value.answers } : null
  );
  // The correction form stays only while the mode is on or a change made with it waits for Salvar,
  // so it alone says whether the marks show and whether the next save goes out as a correction.
  const marks = computed(() => (stored.value ? liveCorrectionMarks(stored.value, answers.value) : null));
  const changeCount = computed(() => countCorrectionChanges(marks.value));

  // Undoing the last pending change by hand, with the mode off, closes the correction as leaving
  // the mode with nothing pending does; otherwise the sheet stays open with no ring and the next
  // ordinary edit would go out as a correction. Synchronous, so no render sees the stale form.
  watch(
    changeCount,
    (count) => {
      if (count === 0 && !active.value && !loading.value) form.value = null;
    },
    { flush: 'sync' }
  );

  // A change still waiting for Salvar goes out as a correction even after the mode is left, so the
  // sheet keeps checking it as one: the same open fields and the same required fields the server
  // checks for a correction.
  function compileOptions(): CompileOptions | null {
    if (!form.value || !stored.value) return null;
    return correctionCompileOptions(form.value, stored.value, answers.value);
  }

  function pulse(): void {
    clearTimeout(pulseTimer);
    pulsing.value = false;
    if (prefersReducedMotion()) return;
    pulsing.value = true;
    pulseTimer = setTimeout(() => {
      pulsing.value = false;
    }, PULSE_MS);
  }

  async function fetchCorrectionForm(id: number): Promise<boolean> {
    const request = generation;
    loading.value = true;
    try {
      const loaded = await getGeneratedRecordForm(id, { isCorrection: true });
      if (request !== generation) return false;
      const full = { ...createEmptyAnswers(loaded.matrix.id), ...loaded.answers };
      const opened = openedAnswers.value ?? full;
      answers.value = mergeUnsavedEdits(full, opened, answers.value);
      openedAnswers.value = full;
      form.value = loaded;
      return true;
    } catch {
      if (request === generation) showErrorToast({ title: CORRECTION_ENTER_ERROR_TITLE, type: 'error' });
      return false;
    } finally {
      if (request === generation) loading.value = false;
    }
  }

  async function enter(): Promise<void> {
    if (loading.value || !recordId || !source.canToggle()) return;
    if (!form.value && !(await fetchCorrectionForm(Number(recordId.value)))) return;
    active.value = true;
    pulse();
  }

  function leave(): void {
    if (loading.value) return;
    active.value = false;
    clearTimeout(pulseTimer);
    pulsing.value = false;
    if (changeCount.value === 0) form.value = null;
  }

  function toggle(): void {
    if (loading.value) return;
    if (active.value) leave();
    else void enter();
  }

  function reset(): void {
    generation += 1;
    loading.value = false;
    form.value = null;
    active.value = false;
    clearTimeout(pulseTimer);
    pulsing.value = false;
  }

  function onKeydown(event: KeyboardEvent): void {
    if (!event.ctrlKey || event.key !== 'F8' || userStore?.isSuperuser !== true) return;
    event.preventDefault();
    if (!event.repeat) toggle();
  }

  if (recordId) window.addEventListener('keydown', onKeydown);

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown);
    reset();
  });

  return {
    form,
    active,
    pulsing,
    loading,
    changeCount,
    marks,
    stored,
    compileOptions,
    toggle,
    leave,
    reset,
  };
}

export type CorrectionMode = ReturnType<typeof useCorrectionMode>;
